import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useApp } from '../App';
import { pricingUrl } from '../lib/billing';
import { format } from '../lib/i18n';
import { FEATURES, planByCode, type PaidPlan, type PlanFeature } from '../lib/plans';
import { Icon } from './icons';

const MODAL = 'plan-upgrade';

type Upgrade = { open: (feature: PlanFeature) => void };
const UpgradeContext = createContext<Upgrade>({ open: () => undefined });

/** Opens the upgrade pop-up for a locked feature, from anywhere under <UpgradeProvider>. */
export const useUpgrade = () => useContext(UpgradeContext);

/**
 * The upgrade pop-up (reference: Instafeed's "Tag products" dialog): the feature's name, its plan chip and tagline,
 * what the plan includes, the price and trial, and "Start trial", which goes to Shopify's plan page where the
 * merchant approves the subscription. One modal for the whole app.
 */
export function UpgradeProvider({ children }: { children: ReactNode }) {
  const { info, t } = useApp();
  const [feature, setFeature] = useState<PlanFeature>('tags');
  const open = useCallback((next: PlanFeature) => {
    setFeature(next);
    void shopify.modal.show(MODAL);
  }, []);
  const value = useMemo(() => ({ open }), [open]);
  const plan = planByCode(FEATURES[feature].plan);
  const previous = plan.code === 'plus' ? planByCode('pro') : null;
  const start = () => window.open(pricingUrl(info.shop.domain), '_top');

  return (
    <UpgradeContext.Provider value={value}>
      {children}
      <s-modal id={MODAL} heading={t[FEATURES[feature].title]}>
        <s-stack gap="base">
          <s-stack gap="small-200">
            <span><PlanChip plan={plan.code as PaidPlan} static /></span>
            <s-text type="strong">{t[plan.tagline]}</s-text>
          </s-stack>
          <s-divider />
          <s-stack gap="small-200">
            <s-text>{t.whatsIncluded}</s-text>
            <ul className="ux-checks">
              {previous && (
                <li><Icon name="check" /><s-text>{format(t.everythingIn, { plan: previous.name })}</s-text></li>
              )}
              {plan.features.map(key => (
                <li key={key}><Icon name="check" /><s-text>{t[key]}</s-text></li>
              ))}
            </ul>
          </s-stack>
          <s-divider />
          <s-stack gap="small-200">
            <s-stack direction="inline" gap="small-300" alignItems="end">
              <s-text color="subdued">USD</s-text>
              <span className="ux-price">${plan.monthly}</span>
              <s-text color="subdued">{t.perMonth}</s-text>
            </s-stack>
            <s-stack direction="inline" gap="small-200" alignItems="center">
              <s-icon type="check-circle" tone="success" />
              <s-text color="subdued">{format(t.trialCancel, { days: plan.trialDays })}</s-text>
            </s-stack>
          </s-stack>
        </s-stack>
        <s-button slot="primary-action" variant="primary" onClick={start}>
          {format(t.startTrialN, { days: plan.trialDays })}
        </s-button>
        <s-button slot="secondary-actions" commandFor={MODAL} command="--hide">{t.notNow}</s-button>
      </s-modal>
    </UpgradeContext.Provider>
  );
}

type ChipProps = { plan: PaidPlan; feature?: PlanFeature; static?: boolean };

/**
 * PRO / PLUS chip next to a locked control. It's the way in to the upgrade pop-up: the control itself stays
 * disabled (Built for Shopify 4.3.7: locked features look and act disabled, and name the plan that unlocks them).
 */
export function PlanChip({ plan, feature, static: isStatic }: ChipProps) {
  const { t } = useApp();
  const { open } = useUpgrade();
  const name = planByCode(plan).name;
  const className = `ux-plan-chip ux-plan-chip--${plan}`;
  if (isStatic || !feature) return <span className={className}>{name.toUpperCase()}</span>;
  return (
    <button type="button" className={className} aria-label={format(t.planChipLabel, { plan: name })} onClick={() => open(feature)}>
      {name.toUpperCase()}
    </button>
  );
}

/** A locked feature's chip, or nothing when the plan includes it. */
export function FeatureChip({ feature, unlocked }: { feature: PlanFeature; unlocked: boolean }) {
  return unlocked ? null : <PlanChip plan={FEATURES[feature].plan} feature={feature} />;
}

/** A whole page or section the plan doesn't include: what it is, its plan chip, and a way into the pop-up. */
export function LockedCard({ feature, title, text }: { feature: PlanFeature; title: string; text: string }) {
  const { t } = useApp();
  const { open } = useUpgrade();
  const plan = planByCode(FEATURES[feature].plan);
  return (
    <s-section>
      <s-stack gap="small-200">
        <s-stack direction="inline" gap="small-200" alignItems="center">
          <s-heading>{title}</s-heading>
          <PlanChip plan={plan.code as PaidPlan} feature={feature} />
        </s-stack>
        <s-paragraph>{text}</s-paragraph>
        <s-stack direction="inline">
          <s-button icon="lock" onClick={() => open(feature)}>{format(t.upgradeToPlan, { plan: plan.name })}</s-button>
        </s-stack>
      </s-stack>
    </s-section>
  );
}
